import { ref } from "vue";
import { defineStore } from "pinia";
import { Mutex } from "async-mutex";
import { readCookie } from "@/utils/cookies/JsonCookieReader";
import { writeCookie } from "@/utils/cookies/JsonCookieWriter";
import { useCurrentUserStore } from "@/stores/CurrentUserStore";
import { MaintenanceRequestListFilters } from "@/types/maintenance-requests/MaintenanceRequestListFilters";

export const useMaintenanceRequestListFiltersStore = defineStore(
  "maintenanceRequestListFilters",
  () => {
    const mutex = new Mutex();

    const cachedFilters = ref<MaintenanceRequestListFilters>();
    const currentUserStore = useCurrentUserStore();

    /** Constructs filter name for currently logged in user and site prefix */
    const getFilterName = async () => {
      const filterName = "x-maintenance-request-list-filters";
      const currentUser = await currentUserStore.getCurrentUserDetails();
      const sitePrefix = await currentUserStore.getSitePrefix();

      return [filterName, sitePrefix, currentUser.id]
        .filter((item) => !!item)
        .join("-");
    };

    /** Gets the previous Work Order List filters */
    const getFilters = async () => {
      const filters = await mutex.runExclusive(async () => {
        // To improve performance, only read the cookie we don't already have the data
        if (!cachedFilters.value) {
          cachedFilters.value = readCookie<MaintenanceRequestListFilters>(
            await getFilterName(),
          );
        }

        return cachedFilters.value;
      });

      return filters;
    };

    /** Stores the current Work Order List filters so filters are remembered
     * when user navigates back to the Work Order List */
    const updateFilters = async (filters: MaintenanceRequestListFilters) => {
      await mutex.runExclusive(async () => {
        /* Improve performance by caching the updated filters
            /* This means the app won't need to read the cookie unnessarily */
        cachedFilters.value = filters;

        // Update cookie so filters are remembered when user reloads the app
        writeCookie<MaintenanceRequestListFilters>(
          await getFilterName(),
          filters,
        );
      });
    };

    /** Clears the current filters cache */
    const clearCache = () => (cachedFilters.value = undefined);

    return { getFilters, updateFilters, clearCache };
  },
);
